/**
 * processLayoutRenderers — wspólne `contextRenderers` klocków formularza „Proces akceptacji"
 * (`process_view`, historia spraw rekordu) i „Przycisk akceptacji" (`process_action_button`) dla
 * kartotek z osią typu (dokumenty, zadania). Jedno źródło kształtu: gating (zapisany rekord +
 * aktywny moduł Procesy), ustawienia klocka, ta sama bramka zapisu co baner.
 */

import type { ReactNode } from 'react';

import type { LayoutElement } from '@/components/form/RecursiveFormRenderer';
import { parseProcessViewElementConfig } from '@/components/modules/layout-elements/ProcessViewElement';
import type { DecisionGate } from '@/hooks/useSaveBeforeDecision';

import { ProcessActionButton } from './ProcessActionButton';
import { ProcessHistoryTab } from './ProcessHistoryTab';

export interface ProcessLayoutRenderersOptions {
  /** Kod modułu rekordu w obiegu akceptacji (`crm_core.documents`, `crm_core.tasks`). */
  moduleCode: string;
  /** Id zapisanego rekordu; brak = tryb create (klocki bez treści). */
  recordId: string | null | undefined;
  /** Czy moduł Procesy jest aktywny dla tenanta. */
  processesActive: boolean;
  /** Typ rekordu (typ dokumentu / typ zadania) — do wyboru procesu przy „Wyślij do akceptacji". */
  typeId?: string | null;
  /** Ta sama bramka zapisu, którą strona daje banerowi; brak = okno otwiera się od razu. */
  beforeDecision?: DecisionGate;
}

export function buildProcessLayoutRenderers({
  moduleCode,
  recordId,
  processesActive,
  typeId,
  beforeDecision,
}: ProcessLayoutRenderersOptions): Record<
  string,
  (props: { element: LayoutElement }) => ReactNode
> {
  return {
    // `showCancelled` / `showDecisionActions` z ustawień klocka (brak = anulowane ukryte, bez
    // przycisków decyzji).
    process_view: ({ element }) => {
      if (!recordId || !processesActive) return null;
      const cfg = parseProcessViewElementConfig(element.settings);
      return (
        <ProcessHistoryTab
          moduleCode={moduleCode}
          recordId={recordId}
          showCancelled={cfg.showCancelled}
          actions={cfg.showDecisionActions ? { beforeDecision } : null}
        />
      );
    },
    process_action_button: () => {
      if (!recordId || !processesActive) return null;
      return (
        <ProcessActionButton
          moduleCode={moduleCode}
          recordId={recordId}
          typeId={typeId}
          beforeDecision={beforeDecision}
        />
      );
    },
  };
}
